// Typed Firestore wrappers. Components must import from this module rather
// than calling Firestore SDK directly — keeps query shapes auditable and
// makes it easy to swap to emulator paths in tests.

import { collection, collectionGroup, doc, type DocumentReference } from 'firebase/firestore';
import { db } from '../firebase';
import type { Org, OrgMember, Service, UserProfile } from './types';

export const userDoc = (userId: string) =>
  doc(db, 'users', userId) as DocumentReference<UserProfile>;

export const orgDoc = (orgId: string) => doc(db, 'orgs', orgId) as DocumentReference<Org>;

export const orgMembersCol = (orgId: string) => collection(db, 'orgs', orgId, 'members');
export const orgMemberDoc = (orgId: string, userId: string) =>
  doc(db, 'orgs', orgId, 'members', userId) as DocumentReference<OrgMember>;

export const orgServicesCol = (orgId: string) => collection(db, 'orgs', orgId, 'services');
export const orgServiceDoc = (orgId: string, serviceId: string) =>
  doc(db, 'orgs', orgId, 'services', serviceId) as DocumentReference<Service>;

export const orgEscalationPoliciesCol = (orgId: string) =>
  collection(db, 'orgs', orgId, 'escalationPolicies');
export const orgEscalationPolicyDoc = (orgId: string, policyId: string) =>
  doc(db, 'orgs', orgId, 'escalationPolicies', policyId);

export const orgIncidentsCol = (orgId: string) => collection(db, 'orgs', orgId, 'incidents');
export const orgIncidentDoc = (orgId: string, incidentId: string) =>
  doc(db, 'orgs', orgId, 'incidents', incidentId);
export const orgIncidentEventsCol = (orgId: string, incidentId: string) =>
  collection(db, 'orgs', orgId, 'incidents', incidentId, 'events');

export const orgIntegrationsCol = (orgId: string) =>
  collection(db, 'orgs', orgId, 'integrations');
export const orgIntegrationDoc = (orgId: string, integrationId: string) =>
  doc(db, 'orgs', orgId, 'integrations', integrationId);

export const orgSchedulesCol = (orgId: string) =>
  collection(db, 'orgs', orgId, 'schedules');
export const orgScheduleDoc = (orgId: string, scheduleId: string) =>
  doc(db, 'orgs', orgId, 'schedules', scheduleId);

export const orgTeamsCol = (orgId: string) =>
  collection(db, 'orgs', orgId, 'teams');
export const orgTeamDoc = (orgId: string, teamId: string) =>
  doc(db, 'orgs', orgId, 'teams', teamId);

export const orgPendingInvitesCol = (orgId: string) =>
  collection(db, 'orgs', orgId, 'pendingInvites');

export const orgPendingInviteDoc = (orgId: string, inviteId: string) =>
  doc(db, 'orgs', orgId, 'pendingInvites', inviteId);

// Reserved for a future "find my invites" feature that needs cross-org
// scanning. Currently invites are accepted via shared-URL flow, so the
// app reads them by exact path and doesn't need the group helper.
export const pendingInvitesGroup = () => collectionGroup(db, 'pendingInvites');
